<script setup lang="ts">
import { ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const props = defineProps<{
  disable?: boolean
  handleNext?: (v$: any) => void
  handlePrev?: () => void
}>()

const { umsokn } = useUmsokn()

const orkuPercent = ref([
  '0%',
  '25%',
  '50%',
  '75%',
  '100%'
])

const rules = {
  umsokn: {
    orkuFrom: { required },
    orkuTo: { required },
    orkuReason: { required }
  }
}

const v$ = useVuelidate(rules, { umsokn })

function todayISO() {
  return new Date().toISOString().split('T')[0]
}

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <h2 class="form__header">{{ $t('ororku_umsokn_step7_header') }}</h2>

      <!-- From / To dates -->
      <v-row>
        <v-col cols="12" md="6">
          <DatePicker
            v-model="umsokn.orkuFrom"
            :label="$t('ororku_umsokn_step7_ororka_from')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.orkuFrom.$error"
            :error-messages="v$.umsokn.orkuFrom.$error ? [$t('ororku_umsokn_step7_ororka_from_error')] : []"
          />
        </v-col>
        <v-col cols="12" md="6">
          <DatePicker
            v-model="umsokn.orkuTo"
            :label="$t('ororku_umsokn_step7_ororka_to')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.orkuTo.$error"
            :error-messages="v$.umsokn.orkuTo.$error ? [$t('ororku_umsokn_step7_ororka_from_error')] : []"
          />
        </v-col>
      </v-row>

      <!-- Reason -->
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.orkuReason"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            :label="$t('ororku_umsokn_step7_orkuReason')"
            :error="v$.umsokn.orkuReason.$error"
            :error-messages="v$.umsokn.orkuReason.$error ? [$t('ororku_umsokn_step7_orkuReason_error')] : []"
          />
        </v-col>
      <!-- Work amount -->
        <v-col cols="12" md="6 ">
          <v-select
            v-model="umsokn.orkuWorkAmountNotHome"
            :items="orkuPercent"
            variant="outlined"
            density="comfortable"
            :label="$t('ororku_umsokn_step7_orkuWorkAmountNotHome')"
            :readonly="props.disable"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6" style="margin-top: -12px;">
          <v-select
            v-model="umsokn.orkuWorkHome"
            :items="orkuPercent"
            variant="outlined"
            density="comfortable"
            :label="$t('ororku_umsokn_step7_orkuWorkHome')"
            :readonly="props.disable"
          />
        </v-col>
      </v-row>

      <!-- Working status -->
      <v-row>
        <v-col cols="12">
          <p>{{ $t('ororku_umsokn_step7_orkuWorking') }}</p>
          <v-radio-group v-model="umsokn.orkuWorking" :readonly="props.disable" inline hide-details>
            <v-radio :label="$t('ororku_umsokn_step7_orkuWorking_yes')" :value="true" />
            <v-radio :label="$t('ororku_umsokn_step7_orkuWorking_no')" :value="false" />
          </v-radio-group>
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-if="umsokn.orkuWorking">
          <v-row>
            <v-col cols="12" md="5">
              <v-text-field
                v-model="umsokn.orkuWorkName"
                variant="outlined"
                density="comfortable"
                :readonly="props.disable"
                :label="$t('ororku_umsokn_step7_orkuWorkName')"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model="umsokn.orkuWorkPlace"
                variant="outlined"
                density="comfortable"
                :readonly="props.disable"
                :label="$t('ororku_umsokn_step7_orkuWorkPlace')"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                v-model="umsokn.orkuWorkHours"
                variant="outlined"
                density="comfortable"
                :readonly="props.disable"
                :label="$t('ororku_umsokn_step7_orkuWorkHours')"
              />
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>

      <!-- Jobs -->
      <v-row>
        <v-col cols="12" md="12">
          <v-text-field
            v-model="umsokn.orkuWorkJobs"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            :label="$t('ororku_umsokn_step7_orkuWorkJobs')"
          />
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style lang="scss" scoped>
input {
  width: 100%;
}
.forms__fadein {
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
}
.forms__fadeout {
  opacity: 1;
}
.slideup,
.slidedown {
  max-height: 0;
  overflow: hidden;
  width: 100%;
  transition: max-height 0.8s ease-in-out;
}
.slidedown {
  max-height: 500px;
}
</style>
